import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { sendEmailVerification } from 'firebase/auth';
import { toast } from 'react-toastify';
import { auth } from '../../firebase/config';
import { createTenant, setActiveTenant } from '../../services/firebase.service';
import { useAuth } from '../../contexts/AuthContext';
import {
  getEmailActionCodeSettings,
  hasEmailVerificationBeenSentThisSession,
  markEmailVerificationSentInSession
} from '../../utils/emailVerification';
import Button from '../../components/common/Button';
import AuthShell from '../../components/auth/AuthShell';
import BusinessTypeSelector from '../../components/configuracion/BusinessTypeSelector';
import { normalizeBusinessType } from '../../utils/businessTypeCatalog';
import { normalizeCommercialName } from '../../utils/normalizeCommercialName';

const VerificarEmailEmpresa = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { completeCompanyAfterVerification, orgId, currentUser, refreshAuthSession, logout } = useAuth();
  const modeFromNav = location.state?.signupMode;
  const demoPhoneFromNav = location.state?.demoPhone;
  const [demoPhone, setDemoPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const submittingRef = useRef(false);
  const [reenviando, setReenviando] = useState(false);
  /** idle | sent | failed — envío automático al entrar (usuarios dados de alta por admin) */
  const [autoSendState, setAutoSendState] = useState('idle');
  const [businessType, setBusinessType] = useState('');
  const [shouldLoadStarterCatalog, setShouldLoadStarterCatalog] = useState(true);
  const [manualNombreInput, setManualNombreInput] = useState('');
  const [needsNombreFallback, setNeedsNombreFallback] = useState(false);

  const signupMode = useMemo(() => {
    const fromState = modeFromNav;
    const fromQuery = new URLSearchParams(location.search || '').get('mode');
    const fromSession = sessionStorage.getItem('pendingSignupMode') || '';
    const pendingDemoPhone = sessionStorage.getItem('pendingDemoPhone') || '';
    const linkedPhone = String(auth.currentUser?.phoneNumber || '').trim();
    const looksLikeDemoEmail = /@nexopos\.demo\.local$/i.test(
      String(auth.currentUser?.email || currentUser?.email || '').trim()
    );
    if (
      fromState === 'demo' ||
      fromQuery === 'demo' ||
      fromSession === 'demo' ||
      Boolean(String(pendingDemoPhone || '').trim()) ||
      Boolean(linkedPhone) ||
      looksLikeDemoEmail
    ) {
      return 'demo';
    }
    return 'standard';
  }, [currentUser?.email, location.search, modeFromNav]);
  const isDemoMode = signupMode === 'demo';
  /** Invitado por admin: claims/usuariosOrg ya traen empresa — no mostrar alta de tenant. */
  const claimOrgId = String(
    currentUser?.orgId ||
      currentUser?.companyId ||
      currentUser?.customClaims?.companyId ||
      currentUser?.customClaims?.orgId ||
      ''
  ).trim();
  /** Flujo nueva empresa vs usuario que ya tiene organización (invitado / claims). */
  const modoNuevaEmpresa = useMemo(
    () => isDemoMode || (!orgId && !claimOrgId),
    [isDemoMode, orgId, claimOrgId]
  );

  useEffect(() => {
    if (isDemoMode) {
      sessionStorage.setItem('pendingSignupMode', 'demo');
    }
  }, [isDemoMode]);

  useEffect(() => {
    const fromSession = sessionStorage.getItem('pendingDemoPhone') || '';
    const normalized = String(demoPhoneFromNav || fromSession || '').replace(/\D/g, '');
    setDemoPhone(normalized);
    if (normalized) {
      sessionStorage.setItem('pendingDemoPhone', normalized);
    }
  }, [demoPhoneFromNav]);

  useEffect(() => {
    if (isDemoMode) return;
    let fromLocal = {};
    try {
      fromLocal = JSON.parse(localStorage.getItem('pendingEmpresaOnboarding') || '{}') || {};
    } catch {
      fromLocal = {};
    }
    const nombreHydrated =
      (location.state?.empresaNombre && String(location.state.empresaNombre).trim()) ||
      (sessionStorage.getItem('pendingEmpresaNombre') || '').trim() ||
      (fromLocal.nombre && String(fromLocal.nombre).trim()) ||
      '';
    const btHydrated =
      normalizeBusinessType(location.state?.businessType) ||
      normalizeBusinessType(sessionStorage.getItem('pendingBusinessType')) ||
      normalizeBusinessType(fromLocal.businessType) ||
      '';
    if (nombreHydrated) {
      setManualNombreInput(nombreHydrated);
      setNeedsNombreFallback(false);
      try {
        sessionStorage.setItem('pendingEmpresaNombre', nombreHydrated);
      } catch {}
    } else {
      setNeedsNombreFallback(true);
    }
    if (btHydrated) {
      setBusinessType(btHydrated);
      try {
        sessionStorage.setItem('pendingBusinessType', btHydrated);
      } catch {}
    }
  }, [isDemoMode, location.state]);


  /**
   * Alta por admin: Firebase no envía solo con createUser en servidor; aquí disparamos
   * un único envío por pestaña si aún no hubo correo (registro propio ya marca la sesión).
   */
  useEffect(() => {
    if (isDemoMode) return undefined;
    if (currentUser?.emailVerified || auth.currentUser?.emailVerified) return undefined;
    if (hasEmailVerificationBeenSentThisSession()) {
      setAutoSendState('sent');
      return undefined;
    }
    const user = auth.currentUser;
    if (!user?.email) return undefined;

    let cancelled = false;
    (async () => {
      try {
        auth.languageCode = 'es';
        await sendEmailVerification(user, getEmailActionCodeSettings());
        if (cancelled) return;
        markEmailVerificationSentInSession();
        setAutoSendState('sent');
        toast.success(
          'Te enviamos el correo de verificación. Revisá spam o promociones si no lo ves en unos minutos.',
          { autoClose: 7000 }
        );
      } catch (err) {
        if (cancelled) return;
        setAutoSendState('failed');
        console.warn('[VerificarEmail] auto sendEmailVerification:', err?.message || err);
        toast.warning(
          'No pudimos enviar el correo automáticamente (límite de Firebase o red). Usá «Reenviar correo de verificación» abajo.',
          { autoClose: 8000 }
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isDemoMode, currentUser?.emailVerified]);

  const normalizeDemoPhone = (raw) => String(raw || '').replace(/\D/g, '');

  useEffect(() => {
    const canContinueByVerification =
      Boolean(currentUser?.emailVerified) ||
      Boolean(String(currentUser?.phoneNumber || '').trim()) ||
      isDemoMode;
    if (
      location.pathname !== '/verificar-email' ||
      !orgId ||
      !canContinueByVerification
    ) {
      return;
    }
    if ((sessionStorage.getItem('pendingEmpresaNombre') || '').trim()) {
      return;
    }
    if (sessionStorage.getItem('postVerifyGoConfig')) {
      return;
    }
    navigate('/', { replace: true });
  }, [orgId, currentUser?.emailVerified, currentUser?.phoneNumber, isDemoMode, location.pathname, navigate]);

  const reenviar = async () => {
    try {
      setReenviando(true);
      const user = auth.currentUser;
      if (!user?.email) {
        navigate('/login', { replace: true });
        return;
      }
      auth.languageCode = 'es';
      await sendEmailVerification(user, getEmailActionCodeSettings());
      markEmailVerificationSentInSession();
      setAutoSendState('sent');
      toast.success(
        `Listo: reenviamos el correo de seguridad a ${user.email}. Buscá "Verificación de correo electrónico", abrilo y confirmá para proteger la cuenta de tu negocio.`,
        { autoClose: 8000 }
      );
    } catch (err) {
      toast.error(err.message || 'No se pudo reenviar el correo. Probá dentro de unos minutos.');
    } finally {
      setReenviando(false);
    }
  };

  const continuarYaVerificado = async () => {
    if (loading || submittingRef.current) return;
    submittingRef.current = true;
    try {
      setLoading(true);
      if (!auth.currentUser) {
        navigate('/login', { replace: true });
        return;
      }
      const { emailVerified } = await refreshAuthSession();
      if (!isDemoMode && !emailVerified) {
        toast.warning(
          'Tu correo todavía no está verificado. Este paso de seguridad evita accesos no autorizados y protege datos de clientes y ventas.'
        );
        return;
      }
      toast.success(
        isDemoMode
          ? 'Listo. Estamos preparando tu demo express.'
          : 'Correo verificado correctamente. Ya podés continuar.'
      );

      if (!modoNuevaEmpresa) {
        navigate('/', { replace: true });
        return;
      }

      if (!isDemoMode && !normalizeBusinessType(businessType)) {
        toast.error('Seleccioná el tipo de comercio antes de continuar.');
        return;
      }

      const nombreDemo = `Demo ${String(demoPhone || '').slice(-4) || 'express'}`;
      let nombre = '';
      if (isDemoMode) {
        nombre = nombreDemo;
      } else {
        let fromLocalNombre = '';
        try {
          fromLocalNombre = (JSON.parse(localStorage.getItem('pendingEmpresaOnboarding') || '{}') || {}).nombre || '';
        } catch {
          fromLocalNombre = '';
        }
        const resolvedNombre = normalizeCommercialName(
          location.state?.empresaNombre ||
            sessionStorage.getItem('pendingEmpresaNombre') ||
            fromLocalNombre ||
            manualNombreInput
        );
        if (!resolvedNombre.ok) {
          setNeedsNombreFallback(true);
          toast.error(resolvedNombre.error || 'Ingresá el nombre de tu empresa para continuar.');
          return;
        }
        nombre = resolvedNombre.value;
        setNeedsNombreFallback(false);
      }
      const slug = nombre ? nombre.toLowerCase().replace(/\s+/g, '-') : null;
      const chosenPlan = sessionStorage.getItem('pendingChosenPlan') || 'basic';
      if (!isDemoMode) {
        toast.info('Creando tu empresa…', { autoClose: 4000 });
      }
      const res = await createTenant(
        nombre,
        slug,
        '',
        chosenPlan,
        {
          creationMode: isDemoMode ? 'demo_express' : 'standard',
          demoPhone: isDemoMode ? normalizeDemoPhone(demoPhone) : '',
          businessType: isDemoMode ? undefined : businessType,
          shouldLoadStarterCatalog: isDemoMode ? undefined : shouldLoadStarterCatalog
        }
      );
      if (!res?.success || !res.orgId) {
        throw new Error('No se pudo crear la empresa');
      }
      try {
        await setActiveTenant(res.orgId);
      } catch (setActiveErr) {
        const setActiveCode = String(setActiveErr?.code || '');
        if (!(isDemoMode && setActiveCode.includes('failed-precondition'))) {
          throw setActiveErr;
        }
      }
      await completeCompanyAfterVerification();
      sessionStorage.removeItem('pendingEmpresaNombre');
      sessionStorage.removeItem('pendingBusinessType');
      sessionStorage.removeItem('pendingChosenPlan');
      sessionStorage.removeItem('pendingSignupMode');
      sessionStorage.removeItem('pendingDemoPhone');
      try {
        localStorage.removeItem('pendingEmpresaOnboarding');
      } catch {}
      if (isDemoMode) {
        toast.success(
          'Gracias por probar 0E3 POS. Tu demo full está activa por 48 hs: recorrê todo y cuando quieras seguís con un plan pago.',
          { autoClose: 7000 }
        );
        navigate('/', { replace: true });
      } else {
        if (res.reused) {
          toast.success('Tu empresa ya estaba creada. Continuamos con la configuración.', { autoClose: 5500 });
        } else {
          toast.success('Tu correo fue verificado. Ahora terminemos de configurar tu empresa.', { autoClose: 5500 });
          toast.success('Tu empresa ya está lista. Tenés 30 días de acceso completo para comenzar a trabajar con 0E3 POS.', {
            autoClose: 7000
          });
        }
        sessionStorage.setItem('postVerifyGoConfig', '1');
        navigate('/configuracion/empresa', {
          replace: true,
          state: {
            openWizardModal: true,
            onboardingSource: 'standard'
          }
        });
      }
    } catch (err) {
      const codigo = err?.code || '';
      let msg = err?.message || 'No se pudo crear la empresa';
      if (codigo === 'functions/failed-precondition' && /verificar|correo/i.test(msg)) {
        msg = 'Tu correo aún no está verificado en el servidor.';
      } else if (codigo === 'functions/aborted' || /aún está en curso/i.test(msg)) {
        msg = 'El alta de tu empresa aún está en curso. Reintentá en unos segundos.';
      } else if (codigo === 'functions/permission-denied') {
        msg = isDemoMode
          ? err.message || 'No se pudo activar la demo.'
          : err.message || 'No se pudo completar el alta.';
      }
      toast.error(msg);
    } finally {
      submittingRef.current = false;
      setLoading(false);
    }
  };

  const volverAlInicioSesion = async () => {
    try {
      await logout();
    } catch {
      // Ignorar error de cierre para permitir navegación al login.
    } finally {
      sessionStorage.removeItem('pendingEmpresaNombre');
      sessionStorage.removeItem('postVerifyGoConfig');
      navigate('/login', { replace: true });
    }
  };

  return (
    <AuthShell
      maxWidthClass="max-w-lg"
      eyebrow={modoNuevaEmpresa ? (isDemoMode ? 'Demo express 48 hs' : 'Nueva empresa') : 'Tu cuenta 0E3 POS'}
      title={
        isDemoMode
          ? 'Confirmá tu demo y entrá al sistema'
          : 'Verificá tu correo para activar tu cuenta segura'
      }
      subtitle={
        isDemoMode
          ? 'En demo express simplificamos todo: cargás tu celular, confirmás y entrás directo al sistema con datos de prueba cargados.'
          : null
      }
    >
      {!isDemoMode ? (
        <>
          <p className="text-sm text-e0e3-muted leading-relaxed">
            {autoSendState === 'failed' ? (
              <>
                No pudimos enviar el correo automáticamente al entrar. Usá{' '}
                <strong className="text-e0e3-foreground">Reenviar correo de verificación</strong> abajo y revisá spam.
              </>
            ) : (
              <>
                Te enviamos (o estás por recibir) un mensaje al correo{' '}
                <strong className="text-e0e3-foreground">{currentUser?.email || auth.currentUser?.email}</strong>.
              </>
            )}
          </p>
          <ul className="mt-4 text-sm text-e0e3-muted space-y-2 list-disc pl-5">
            <li>Buscá el correo con asunto <strong className="text-e0e3-foreground">«Verificación de correo electrónico»</strong>.</li>
            <li>Presioná el botón <strong className="text-e0e3-foreground">Verificar el correo</strong>.</li>
            <li>Revisá <strong className="text-e0e3-foreground">spam</strong> y promociones si no lo ves.</li>
            <li>Volvé aquí y presioná <strong className="text-e0e3-foreground">Ya verifiqué el correo</strong>.</li>
          </ul>
          <p className="mt-3 rounded-xl border border-e0e3-accent/25 bg-e0e3-accent/10 px-3 py-2 text-xs text-sky-100">
            Esta validación protege el acceso al sistema y la información comercial de tus clientes.
          </p>
          <p className="mt-4 text-sm text-e0e3-muted leading-relaxed">
            Antes de continuar, indicá el rubro de tu negocio. Así preparamos el catálogo correcto.
          </p>
          {modoNuevaEmpresa ? (
            <div className="mt-6 space-y-4">
              {(needsNombreFallback || !normalizeCommercialName(manualNombreInput).ok) ? (
                <div className="space-y-2">
                  <label className="block text-xs font-semibold uppercase tracking-wide text-e0e3-muted">
                    Nombre de tu empresa
                  </label>
                  <input
                    type="text"
                    className="nexo-field py-3"
                    value={manualNombreInput}
                    onChange={(e) => {
                      setManualNombreInput(e.target.value);
                      setNeedsNombreFallback(true);
                    }}
                    placeholder="Ej: Distribuidora Norte"
                  />
                  <p className="text-xs text-amber-200/90">Ingresá el nombre de tu empresa para continuar.</p>
                </div>
              ) : null}
              <BusinessTypeSelector
                businessType={businessType}
                onBusinessTypeChange={setBusinessType}
                shouldLoadStarterCatalog={shouldLoadStarterCatalog}
                onShouldLoadStarterCatalogChange={setShouldLoadStarterCatalog}
                idPrefix="verify-business-type"
              />
            </div>
          ) : null}
        </>
      ) : null}

      {modoNuevaEmpresa && isDemoMode ? (
        <>
          <div className="mt-2 rounded-xl border border-e0e3-ok/30 bg-e0e3-ok/10 p-3 text-sm text-emerald-100">
            <p className="font-semibold">Demo express full por 48 horas</p>
            <p className="mt-1 text-xs text-emerald-200/90">
              Datos de ejemplo cargados (clientes, ventas, compras, gastos y reportes).
              Reingreso: usuario = celular, contraseña = celular.
            </p>
            {demoPhone ? (
              <p className="mt-1 text-xs text-emerald-200/80">Celular demo: {demoPhone}</p>
            ) : null}
          </div>
          <div className="mt-6 space-y-3">
            <label className="block text-xs font-semibold uppercase tracking-wide text-e0e3-muted">
              Celular para activar demo
            </label>
            <input
              type="tel"
              className="nexo-field py-3"
              value={demoPhone}
              onChange={(e) => setDemoPhone(String(e.target.value || '').replace(/\D/g, ''))}
              placeholder="Ej: 3764123456"
            />
            <p className="text-xs text-e0e3-muted">
              Una sola prueba por celular cada 48 hs en este entorno.
            </p>
          </div>
        </>
      ) : null}

      <div className="mt-8 flex flex-col gap-3">
        <Button
          type="button"
          fullWidth
          loading={loading}
          disabled={loading}
          onClick={continuarYaVerificado}
        >
          {loading
            ? isDemoMode
              ? 'Activando demo…'
              : 'Creando tu empresa…'
            : isDemoMode
              ? 'Activar demo express'
              : 'Ya verifiqué el correo — continuar'}
        </Button>
        {!isDemoMode ? (
          <button
            type="button"
            disabled={reenviando}
            onClick={reenviar}
            className="w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-sm font-semibold text-e0e3-foreground transition hover:bg-white/10 disabled:opacity-50"
          >
            {reenviando ? 'Reenviando…' : 'Reenviar correo de verificación'}
          </button>
        ) : null}
        <div className="text-center text-sm text-e0e3-muted">
          ¿Problemas?{' '}
          <button
            type="button"
            onClick={volverAlInicioSesion}
            className="font-semibold text-e0e3-accent hover:text-e0e3-accentSoft"
          >
            Volver al inicio de sesión
          </button>
        </div>
      </div>
    </AuthShell>
  );
};

export default VerificarEmailEmpresa;
